import {
  AlertOctagon,
  AlertTriangle,
  ArrowRight,
  Banknote,
  Bell,
  BellOff,
  CheckCircle2,
  Clock,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useTransitionStatus } from '@/hooks/use-loans';
import { useOutstanding } from '@/hooks/use-payments';
import { handleMutationError } from '@/lib/error-handling';
import { LOAN_STATUS_GUIDANCE } from '@/lib/loan-status-guidance';
import { toast } from '@/stores/toast-store';
import { LOAN_STATUS_CONFIG, type LoanStatus } from '@/types';

interface StatusTransitionModalProps {
  open: boolean;
  onClose: () => void;
  loanId: string;
  loanNumber: string;
  currentStatus: LoanStatus;
  targetStatus?: LoanStatus;
  availableStatuses: LoanStatus[];
  onSelectStatus: (status: LoanStatus) => void;
  borrowerName?: string;
  principal?: string;
}

/**
 * Contextual metadata for key transitions — these are the transitions
 * that require extra confirmation or have important side-effects.
 */
const TRANSITION_META: Partial<
  Record<
    string,
    {
      icon: React.ReactNode;
      title: string;
      description: string;
      color: string;
      bgColor: string;
      borderColor: string;
      requiresReason: boolean;
      warningMessage?: string;
    }
  >
> = {
  APPROVED: {
    icon: <ShieldCheck className="w-5 h-5 text-green-600" />,
    title: 'Approve Loan',
    description:
      'This loan will be marked as approved and ready for disbursement. The approval date will be recorded.',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    requiresReason: false,
  },
  DISBURSED: {
    icon: <Banknote className="w-5 h-5 text-blue-600" />,
    title: 'Disburse Loan',
    description:
      'Funds will be marked as disbursed. The disbursement date will be recorded and the loan will become active.',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    requiresReason: false,
    warningMessage: 'Ensure funds have been transferred to the borrower before proceeding.',
  },
  CANCELLED: {
    icon: <XCircle className="w-5 h-5 text-red-600" />,
    title: 'Cancel Loan',
    description:
      'This loan will be permanently cancelled and the closed date will be recorded. This action cannot be easily reversed.',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    requiresReason: true,
    warningMessage:
      'Cancelling a loan is a significant action. Please provide a reason for the cancellation.',
  },
  PAID_OFF: {
    icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
    title: 'Mark as Paid Off',
    description: 'This loan will be marked as fully paid off. The closed date will be recorded.',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    requiresReason: false,
  },
  DEFAULT: {
    icon: <AlertOctagon className="w-5 h-5 text-red-700" />,
    title: 'Mark as Default',
    description:
      'This loan will be marked as in default. The default date will be recorded and the default interest rate may apply.',
    color: 'text-red-800',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
    requiresReason: true,
    warningMessage:
      "Defaulting a loan is a serious action that affects the borrower's record. Ensure all collection efforts have been documented.",
  },
  UNDER_REVIEW: {
    icon: <Clock className="w-5 h-5 text-amber-600" />,
    title: 'Submit for Review',
    description: 'This loan application will be submitted for review by a manager or officer.',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    requiresReason: false,
  },
};

export function StatusTransitionModal({
  open,
  onClose,
  loanId,
  loanNumber,
  currentStatus,
  targetStatus,
  availableStatuses,
  onSelectStatus,
  borrowerName,
  principal,
}: StatusTransitionModalProps) {
  const transition = useTransitionStatus();
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [notifyBorrower, setNotifyBorrower] = useState(true);
  const isPaidOffTransition = targetStatus === 'PAID_OFF';
  const { data: outstanding, isLoading: isOutstandingLoading } = useOutstanding(
    isPaidOffTransition ? loanId : '',
  );

  if (!open) return null;

  const meta = targetStatus ? TRANSITION_META[targetStatus] : undefined;
  const fromLabel = LOAN_STATUS_CONFIG[currentStatus]?.label ?? currentStatus;
  const toLabel = targetStatus ? (LOAN_STATUS_CONFIG[targetStatus]?.label ?? targetStatus) : '';
  const toColor = targetStatus ? (LOAN_STATUS_CONFIG[targetStatus]?.color ?? '#6b7280') : '#6b7280';
  const totalOutstanding = outstanding?.totalOutstanding ?? 0;
  const pendingCount = outstanding?.scheduleProgress.pending ?? 0;
  const partialCount = outstanding?.scheduleProgress.partial ?? 0;
  const overdueCount = outstanding?.scheduleProgress.overdue ?? 0;
  const scheduleTotal = outstanding?.scheduleProgress.total ?? 0;
  const hasRepaymentSchedule = scheduleTotal > 0;
  const isPaidOffChecking = isPaidOffTransition && (isOutstandingLoading || !outstanding);
  const isPaidOffNoSchedule = isPaidOffTransition && !!outstanding && !hasRepaymentSchedule;
  const isPaidOffBlocked =
    isPaidOffTransition &&
    !!outstanding &&
    (!hasRepaymentSchedule ||
      totalOutstanding > 0.01 ||
      pendingCount > 0 ||
      partialCount > 0 ||
      overdueCount > 0);
  const disableSubmit =
    transition.isPending ||
    (meta?.requiresReason && !reason.trim()) ||
    (isPaidOffTransition && (isPaidOffChecking || isPaidOffBlocked));
  const supportsBorrowerNotification =
    (currentStatus === 'APPLICATION' && targetStatus === 'UNDER_REVIEW') ||
    (currentStatus === 'UNDER_REVIEW' && targetStatus === 'APPROVED') ||
    (currentStatus === 'APPROVED' && targetStatus === 'DISBURSED') ||
    targetStatus === 'PAID_OFF' ||
    targetStatus === 'CANCELLED';

  function fmtCurrency(value: number) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    }).format(value);
  }

  function handleSubmit() {
    if (!targetStatus) {
      return;
    }

    if (meta?.requiresReason && !reason.trim()) {
      toast.error('A reason is required for this transition');
      return;
    }

    if (isPaidOffTransition && (isOutstandingLoading || !outstanding || isPaidOffBlocked)) {
      toast.error(
        'Loan cannot be marked as Paid Off while outstanding balance or unpaid installments remain.',
      );
      return;
    }

    transition.mutate(
      {
        loanId,
        status: targetStatus,
        reason: reason.trim() || undefined,
        effectiveDate: effectiveDate || undefined,
        notifyBorrower,
      },
      {
        onSuccess: loan => {
          toast.success(`Loan ${loanNumber} moved to ${toLabel}`);
          if (notifyBorrower && loan.borrowerNotification?.status === 'FAILED') {
            toast.error(
              loan.borrowerNotification.reason === 'EMAIL_TRANSPORT_NOT_CONFIGURED'
                ? 'Loan status was updated, but email delivery is not configured.'
                : 'Loan status was updated, but the borrower email could not be sent.',
            );
          }
          handleClose();
        },
        onError: (err: any) => {
          handleMutationError(err, 'Failed to update status');
        },
      },
    );
  }

  function handleClose() {
    setReason('');
    setNotifyBorrower(true);
    onClose();
  }

  if (!targetStatus) {
    return (
      <ModalContentWrapper open={open} onClose={handleClose} title="Update Status">
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{loanNumber}</p>
              {borrowerName && <p className="text-xs text-gray-500">{borrowerName}</p>}
            </div>
            {principal && <p className="text-sm font-semibold text-gray-900">{principal}</p>}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900">Select the next status</p>
            <p className="mt-1 text-xs text-gray-500">
              Only valid transitions from {fromLabel} are available.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {availableStatuses.map(status => {
              const statusConfig = LOAN_STATUS_CONFIG[status];
              const statusGuidance = LOAN_STATUS_GUIDANCE[status];
              return (
                <Button
                  key={status}
                  type="button"
                  variant="secondary"
                  onClick={() => onSelectStatus(status)}
                  className="h-auto w-full justify-start border-gray-200 px-3 py-3 gap-2.5 text-left hover:bg-gray-50"
                >
                  <span
                    className="h-2.5 w-2.5 min-h-2.5 min-w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: statusConfig.color }}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900">
                      {statusConfig.label}
                    </span>
                    <span className="mt-1 block whitespace-normal text-xs font-normal text-gray-500">
                      {statusGuidance.description}
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      </ModalContentWrapper>
    );
  }

  const selectedGuidance = LOAN_STATUS_GUIDANCE[targetStatus];

  return (
    <ModalContentWrapper
      open={open}
      onClose={handleClose}
      title={meta?.title ?? `Move to ${toLabel}`}
    >
      {/* Body */}
      <div className="space-y-4">
        {/* Loan info summary */}
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{loanNumber}</p>
            {borrowerName && <p className="text-xs text-gray-500">{borrowerName}</p>}
          </div>
          {principal && <p className="text-sm font-semibold text-gray-900">{principal}</p>}
        </div>

        {/* Status transition visual */}
        <div className="flex items-center justify-center gap-3 py-2">
          <span
            className="text-xs font-medium px-3 py-1 rounded-full"
            style={{
              backgroundColor: `${LOAN_STATUS_CONFIG[currentStatus]?.color}20`,
              color: LOAN_STATUS_CONFIG[currentStatus]?.color,
            }}
          >
            {fromLabel}
          </span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <span
            className="text-xs font-medium px-3 py-1 rounded-full"
            style={{
              backgroundColor: `${toColor}20`,
              color: toColor,
            }}
          >
            {toLabel}
          </span>
        </div>

        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <p className="text-xs font-semibold text-blue-900">What this status means</p>
          <p className="mt-1 text-sm text-blue-900">{selectedGuidance.description}</p>
          <p className="mt-2 text-xs text-blue-800">
            <span className="font-semibold">Next step:</span> {selectedGuidance.nextStep}
          </p>
        </div>

        {/* Warning */}
        {meta?.warningMessage && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700">{meta.warningMessage}</p>
          </div>
        )}

        {isPaidOffTransition && (
          <div
            className={`rounded-lg border p-3 ${
              isPaidOffChecking
                ? 'border-amber-200 bg-amber-50'
                : isPaidOffBlocked
                  ? 'border-red-200 bg-red-50'
                  : 'border-emerald-200 bg-emerald-50'
            }`}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle
                className={`mt-0.5 h-4 w-4 shrink-0 ${
                  isPaidOffChecking
                    ? 'text-amber-500'
                    : isPaidOffBlocked
                      ? 'text-red-500'
                      : 'text-emerald-600'
                }`}
              />
              <div className="space-y-2 text-xs">
                {isPaidOffChecking ? (
                  <p className="text-amber-700">
                    Checking outstanding balance and installment status before allowing Paid Off.
                  </p>
                ) : isPaidOffNoSchedule ? (
                  <>
                    <p className="font-medium text-red-700">
                      This loan cannot be marked as Paid Off because no repayment schedule exists.
                    </p>
                    <p className="text-red-700">
                      Generate a repayment schedule first so outstanding installments can be
                      verified.
                    </p>
                  </>
                ) : isPaidOffBlocked ? (
                  <>
                    <p className="font-medium text-red-700">
                      This loan cannot be marked as Paid Off because unpaid balance or installments
                      remain.
                    </p>
                    <p className="text-red-700">
                      Outstanding balance: {fmtCurrency(totalOutstanding)}
                    </p>
                    <p className="text-red-700">Pending installments: {pendingCount}</p>
                    <p className="text-red-700">Partial installments: {partialCount}</p>
                    <p className="text-red-700">Overdue installments: {overdueCount}</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-emerald-700">
                      This loan is fully paid and can be marked as Paid Off.
                    </p>
                    <p className="text-emerald-700">
                      Outstanding balance: {fmtCurrency(totalOutstanding)}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Effective date — supports backdating */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Effective Date</label>
          <input
            type="date"
            value={effectiveDate}
            onChange={e => setEffectiveDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Defaults to today. Select a past date to backdate this transition.
          </p>
        </div>

        {/* Reason input */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            {meta?.requiresReason ? 'Reason *' : 'Notes (optional)'}
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder={
              meta?.requiresReason
                ? 'Please provide a reason for this action...'
                : 'Add any notes about this status change...'
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-medium text-gray-900">Borrower notification</p>
              <p className="mt-1 text-xs text-gray-500">
                {!supportsBorrowerNotification
                  ? 'This status transition does not send a borrower email.'
                  : notifyBorrower
                    ? 'The borrower will receive an email about this status update.'
                    : 'No email will be sent to the borrower for this update.'}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setNotifyBorrower(value => !value)}
              disabled={!supportsBorrowerNotification}
              className="w-fit gap-1.5 text-xs"
            >
              {notifyBorrower ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
              {notifyBorrower ? 'Do not notify borrower' : 'Notify borrower'}
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <Button variant="secondary" onClick={handleClose} className="w-fit">
          Cancel
        </Button>
        <Button
          variant="secondary"
          onClick={handleSubmit}
          disabled={disableSubmit}
          className={`w-fit flex items-center gap-2 text-sm font-medium text-white
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                         targetStatus === 'CANCELLED' || targetStatus === 'DEFAULT'
                           ? 'bg-red-700 hover:bg-red-600'
                           : targetStatus === 'APPROVED' || targetStatus === 'PAID_OFF'
                             ? 'bg-green-700 hover:bg-green-600'
                             : 'bg-gray-900 hover:bg-gray-600'
                       }`}
        >
          {transition.isPending && <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />}
          {meta?.title ?? `Move to ${toLabel}`}
        </Button>
      </div>
    </ModalContentWrapper>
  );
}
